// ===========================================================================
// Patient Geography — constantes del módulo.
//
// Fuente única de verdad para colores de banda, etiquetas y formato. Ningún
// componente debe declarar hex sueltos: burbujas, chips, KPIs y panel leen
// BAND_COLORS de acá (UIUX_GEOGRAFIA_PACIENTES.md §3.1).
// ===========================================================================
import { tokens } from "../../shared/clinical-design";

// ---------------------------------------------------------------------------
// Acceso
// ---------------------------------------------------------------------------
//
// Lo decide SOLO la matriz RBAC: módulo `patient_geography` (38) del catálogo,
// mapeado en app/AccessDenied/roleRoutePermissions.js. Para dar acceso a
// alguien, concederle el módulo en Administration → Access Control.
//
// 2026-08-31: se eliminaron de acá `GEOGRAPHY_ALLOWED_EMPLOYEE_IDS` y
// `canUserAccessGeography`, junto con el guard que los aplicaba. Eran del
// piloto, de cuando el módulo no existía en el catálogo, y al registrarse
// quedaron como un segundo gate invisible que hacía que conceder el módulo no
// alcanzara.
//
// ⚠️ DEUDA: `REACT_APP_GEOGRAPHY_EMPLOYEES` sigue definida en .env,
// .env.production y .env.example pero YA NO LA LEE NADIE. Conviene borrar esas
// tres líneas: agregar un id ahí no da acceso, y creer que sí es exactamente
// la confusión que este cambio elimina.

/** Valores por defecto de los query params (coinciden con los del SP). */
export const GEO_DEFAULTS = {
  thresholdMiles: 25,
  /** Semiancho de la banda gris. Fijo en v1 (el backend lo acepta como param). */
  grayMiles: 3,
  locationId: null,
};

/** Rango del slider de umbral. */
export const THRESHOLD_RANGE = { min: 10, max: 50, step: 1 };

/** Debounce del slider antes de refetchear /kpis y /map. */
export const THRESHOLD_DEBOUNCE_MS = 400;

/**
 * Bandas de distancia. Las calcula el SERVIDOR con T (threshold) y G (gray);
 * el front nunca reclasifica — solo pinta lo que llega en `predominantBand` /
 * `distanceBand`.
 */
export const DISTANCE_BAND = {
  NEAR: "NEAR",
  BORDERLINE: "BORDERLINE",
  FAR: "FAR",
  UNKNOWN: "UNKNOWN",
};

/**
 * Color por banda. `tone` mapea al SEMANTIC del design system para reusar
 * StatusPill sin duplicar paleta.
 */
export const BAND_COLORS = {
  [DISTANCE_BAND.NEAR]: { fg: tokens.ok, bg: tokens.okBg, tone: "ok" },
  [DISTANCE_BAND.BORDERLINE]: { fg: tokens.warn, bg: tokens.warnBg, tone: "warn" },
  [DISTANCE_BAND.FAR]: { fg: tokens.crit, bg: tokens.critBg, tone: "crit" },
  [DISTANCE_BAND.UNKNOWN]: { fg: tokens.muted, bg: tokens.mutedBg, tone: "muted" },
};

/** Etiqueta visible por banda (UI en inglés, como el resto de módulos nuevos). */
export const BAND_LABELS = {
  [DISTANCE_BAND.NEAR]: "Near",
  [DISTANCE_BAND.BORDERLINE]: "Borderline",
  [DISTANCE_BAND.FAR]: "Far",
  [DISTANCE_BAND.UNKNOWN]: "No data",
};

/** Orden de severidad (de mayor a menor) para chips y desgloses. */
export const BAND_ORDER = [
  DISTANCE_BAND.NEAR,
  DISTANCE_BAND.BORDERLINE,
  DISTANCE_BAND.FAR,
  DISTANCE_BAND.UNKNOWN,
];

/** Filtro de chips: "ALL" no filtra nada. */
export const BAND_FILTER_ALL = "ALL";

/**
 * Motivos por los que un paciente puede no tener distancia. El tooltip de la
 * burbuja gris debe explicar la causa real, nunca mostrar "0 millas".
 */
export const GEOCODE_STATUS_LABELS = {
  NO_ZIP: "No ZIP code on file",
  ZIP_NOT_FOUND: "ZIP not in the catalog (PO Box)",
  NO_LOCATION: "No assigned center",
  LOCATION_NO_COORDS: "Assigned center has no coordinates",
  MATCHED: "Distance calculated",
};

// ---------------------------------------------------------------------------
// Mapa base
// ---------------------------------------------------------------------------

/**
 * CARTO Positron. Al tile server solo viajan coordenadas del viewport: los
 * datos de pacientes NUNCA salen de nuestra API (§2 del doc de UI/UX).
 * Prohibido Google Maps / Mapbox o cualquier SDK con API key.
 */
export const TILE_URL = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
export const TILE_ATTRIBUTION = "&copy; OpenStreetMap &middot; &copy; CARTO";

/** Encuadre de respaldo (sur de Florida) si ningún centro tiene coordenadas. */
export const FALLBACK_CENTER = [26.7, -80.6];
export const FALLBACK_ZOOM = 7;

// ---------------------------------------------------------------------------
// Formato — reglas transversales (§5.5)
// ---------------------------------------------------------------------------

/**
 * Distancia con 1 decimal. `null` nunca se imprime como 0: es "no data".
 *
 * @param {number|null|undefined} miles
 * @returns {string}
 */
export const fmtMiles = (miles) =>
  miles === null || miles === undefined ? "—" : `${Number(miles).toFixed(1)} mi`;

/**
 * Margen de incertidumbre del centroide del ZIP. Se muestra SIEMPRE junto a la
 * distancia: presentarla como exacta rompe el contrato de honestidad del método.
 *
 * @param {number|null|undefined} uncertainty
 * @returns {string} "± 4.8" o "" si no hay dato
 */
export const fmtUncertainty = (uncertainty) =>
  uncertainty === null || uncertainty === undefined
    ? ""
    : `± ${Number(uncertainty).toFixed(1)}`;

/** Conteos siempre con separador de miles. */
export const fmtCount = (n) => (n ?? 0).toLocaleString("en-US");

/**
 * Radio de la burbuja en píxeles. Escala raíz cuadrada (no lineal) para que un
 * ZIP con 400 pacientes no tape el mapa; clamp a 38px.
 *
 * @param {number} patients
 * @returns {number}
 */
export const bubbleRadius = (patients) =>
  Math.min(38, 6 + Math.sqrt(Math.max(0, patients || 0)) * 1.55);
